<template>
  <ContentWrap>
    <el-alert
      class="mb-12px"
      type="info"
      :closable="false"
      title="配置 Boss 圈「活动」列表页搜索框下方的轮播图；与商城 Banner 相互独立，换图不会影响商城首页。"
    />
    <el-button v-hasPermi="['bosscircle:banner:create']" type="primary" @click="open()">
      <Icon class="mr-5px" icon="ep:plus" />新增轮播
    </el-button>
  </ContentWrap>

  <ContentWrap>
    <el-table v-loading="loading" :data="list" stripe>
      <el-table-column label="图片" width="200">
        <template #default="{ row }">
          <el-image
            :src="row.imageUrl"
            fit="cover"
            style="width: 160px; height: 64px; border-radius: 6px"
            :preview-src-list="[row.imageUrl]"
            preview-teleported
          />
        </template>
      </el-table-column>
      <el-table-column prop="title" label="标题" min-width="140" show-overflow-tooltip />
      <el-table-column label="类型" width="120">
        <template #default="{ row }">
          <el-tag effect="plain">{{ bannerTypeName(row.bannerType) }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="linkUrl" label="跳转地址" min-width="200" show-overflow-tooltip />
      <el-table-column prop="sort" label="排序" width="80" />
      <el-table-column label="状态" width="90">
        <template #default="{ row }">
          <el-tag :type="row.status === 0 ? 'success' : 'info'">
            {{ row.status === 0 ? '启用' : '禁用' }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column label="操作" width="210" fixed="right">
        <template #default="{ row }">
          <el-button
            v-hasPermi="['bosscircle:banner:update']"
            link
            type="primary"
            @click="open(row)"
          >
            编辑
          </el-button>
          <el-button
            v-hasPermi="['bosscircle:banner:update']"
            link
            :type="row.status === 0 ? 'warning' : 'success'"
            @click="toggleStatus(row)"
          >
            {{ row.status === 0 ? '禁用' : '启用' }}
          </el-button>
          <el-button
            v-hasPermi="['bosscircle:banner:delete']"
            link
            type="danger"
            @click="remove(row.id)"
          >
            删除
          </el-button>
        </template>
      </el-table-column>
    </el-table>
  </ContentWrap>

  <Dialog v-model="visible" :title="form.id ? '编辑轮播' : '新增轮播'" width="680px">
    <el-form ref="formRef" :model="form" :rules="rules" label-width="100px">
      <el-form-item label="标题" prop="title">
        <el-input v-model="form.title" maxlength="128" placeholder="请输入标题" />
      </el-form-item>
      <el-form-item label="类型" prop="bannerType">
        <el-select v-model="form.bannerType" class="w-full" placeholder="请选择类型">
          <el-option
            v-for="item in bannerTypes"
            :key="item.value"
            :label="item.label"
            :value="item.value"
          />
        </el-select>
      </el-form-item>
      <el-form-item label="图片" prop="imageUrl">
        <UploadImg v-model="form.imageUrl" />
        <div class="el-upload__tip" style="line-height: 1.4; margin-top: 6px">
          建议尺寸 <b>702 × 280</b>（宽高比约 2.5:1）
        </div>
      </el-form-item>
      <el-form-item label="跳转地址">
        <el-input
          v-model="form.linkUrl"
          maxlength="512"
          placeholder="选填；如 /pages/... 或 https://..."
        />
      </el-form-item>
      <el-form-item label="排序" prop="sort">
        <el-input-number v-model="form.sort" :min="0" />
      </el-form-item>
      <el-form-item label="状态" prop="status">
        <el-radio-group v-model="form.status">
          <el-radio :value="0">启用</el-radio>
          <el-radio :value="1">禁用</el-radio>
        </el-radio-group>
      </el-form-item>
    </el-form>
    <template #footer>
      <el-button @click="visible = false">取消</el-button>
      <el-button type="primary" :loading="saving" @click="save">保存</el-button>
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import type { FormInstance, FormRules } from 'element-plus'
import * as Api from '@/api/business/banner'

defineOptions({ name: 'BossCircleBanner' })

const message = useMessage()
const loading = ref(false)
const saving = ref(false)
const visible = ref(false)
const list = ref<Api.BossCircleBannerVO[]>([])
const formRef = ref<FormInstance>()
const bannerTypes = [
  { label: '活动列表', value: 'ACTIVITY_LIST' },
  { label: '首页侧栏广告', value: 'HOME_SIDE' },
  { label: '帮助中心头图', value: 'ARTICLE_LIST' },
]

const defaults = (): Api.BossCircleBannerVO => ({
  title: '',
  bannerType: 'ACTIVITY_LIST',
  imageUrl: '',
  linkUrl: '',
  sort: 0,
  status: 0
})
const form = reactive<Api.BossCircleBannerVO>(defaults())
const rules: FormRules = {
  title: [{ required: true, message: '请输入标题', trigger: 'blur' }],
  bannerType: [{ required: true, message: '请选择类型', trigger: 'change' }],
  imageUrl: [{ required: true, message: '请上传图片', trigger: 'change' }],
  sort: [{ required: true, message: '请输入排序', trigger: 'blur' }],
  status: [{ required: true, message: '请选择状态', trigger: 'change' }]
}

const bannerTypeName = (value?: string) =>
  bannerTypes.find((item) => item.value === value)?.label || value || '-'

const load = async () => {
  loading.value = true
  try {
    list.value = await Api.getBannerList()
  } finally {
    loading.value = false
  }
}

const open = (row?: Api.BossCircleBannerVO) => {
  Object.assign(form, defaults(), row || {})
  visible.value = true
  nextTick(() => formRef.value?.clearValidate())
}

const save = async () => {
  await formRef.value?.validate()
  saving.value = true
  try {
    form.id ? await Api.updateBanner(form) : await Api.createBanner(form)
    message.success('保存成功')
    visible.value = false
    await load()
  } finally {
    saving.value = false
  }
}

const toggleStatus = async (row: Api.BossCircleBannerVO) => {
  const nextStatus = row.status === 0 ? 1 : 0
  await message.confirm(`确认${nextStatus === 0 ? '启用' : '禁用'}该轮播吗？`)
  await Api.updateBanner({ ...row, status: nextStatus })
  message.success('操作成功')
  await load()
}

const remove = async (id?: number) => {
  if (!id) return
  await message.confirm('确认删除该轮播吗？')
  await Api.deleteBanner(id)
  message.success('删除成功')
  await load()
}

onMounted(load)
</script>
